import { memo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * BookingStepOfferings - Step 1: pick a bookable offering.
 * @param {Array}    offerings         - all official offerings for the entity
 * @param {boolean}  offeringsLoading  - loading state
 * @param {object}   selectedOffering  - currently selected offering (or null)
 * @param {function} onSelect          - called with the chosen offering object
 * @idempotent @atomic
 */
const BookingStepOfferings = memo(function BookingStepOfferings({
  offerings,
  offeringsLoading,
  selectedOffering,
  onSelect,
}) {
  const { t } = useTranslation()
  // Only bookable services — items/packages can be sold but are not bookable appointments
  const pricedOfferings = (offerings || []).filter(
    (o) => o.price != null && (o.offering_type === 'service' || o.offeringType === 'service')
  )

  if (offeringsLoading) {
    return (
      <div className="p-4">
        <p className="text-sm text-gray-400 py-4 text-center">{t('common.loading', 'Loading...')}</p>
      </div>
    )
  }

  if (!pricedOfferings.length) {
    return (
      <div className="p-4">
        <p className="text-sm text-gray-500 py-4 text-center">
          {t('bookings.noBookableOfferings', 'No bookable services listed yet.')}
        </p>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-2">
      {pricedOfferings.map((o) => (
        <button
          key={o.id}
          onClick={() => onSelect(o)}
          onTouchEnd={(e) => { e.preventDefault(); onSelect(o) }}
          className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
            selectedOffering?.id === o.id
              ? 'border-primary bg-primary/5 ring-1 ring-primary'
              : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
          }`}
        >
          <div className="flex justify-between items-start gap-2">
            <span className="font-medium text-sm text-gray-900 flex-1 min-w-0 break-words">{o.name}</span>
            <span className="text-sm font-semibold text-primary flex-shrink-0">${Number(o.price).toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {o.description && (
              <p className="text-xs text-gray-500 flex-1 line-clamp-2">{o.description}</p>
            )}
            {o.durationMinutes && (
              <span className="flex-shrink-0 text-[11px] font-medium text-gray-400 whitespace-nowrap">
                {o.durationMinutes} {t('common.min', 'min')}
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  )
})

export default BookingStepOfferings
